import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import OnboardingTutorial from '@/components/OnboardingTutorial';
import EstimateReviewQueue from '@/components/EstimateReviewQueue';
import { 
  FileText, 
  Receipt, 
  Users, 
  DollarSign, 
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Hammer,
  ClipboardList,
  ClipboardCheck
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import usePullToRefresh from '@/hooks/usePullToRefresh';
import PullToRefreshIndicator from '@/components/PullToRefreshIndicator';

const StatCard = ({ title, value, subValue, icon: Icon, color, link, urgent }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  return (
    <button
      onClick={() => link && navigate(link)}
      className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 text-left w-full group hover:shadow-md transition-all ${urgent ? 'ring-2 ring-red-200' : ''}`}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 overflow-hidden">
          <p className="text-muted-foreground font-semibold text-xs sm:text-xs uppercase tracking-wide mb-1.5 leading-tight">{title}</p>
          <p className="font-heading text-xl sm:text-2xl md:text-2xl font-bold tracking-tight truncate">{value}</p>
          {subValue && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-tight line-clamp-1">{subValue}</p>
          )}
        </div>
        <div 
          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color} group-hover:scale-105 transition-transform`}
        >
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
        </div>
      </div>
      {link && (
        <div className="hidden sm:flex items-center gap-1 text-primary mt-3 sm:mt-4 font-semibold text-xs sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity">
          <span data-testid="view-details-text">{t('view_details')}</span> <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </button>
  );
};

const ActivityItem = ({ activity }) => {
  const getIcon = () => {
    switch (activity.object_type) {
      case 'estimate':
        return <FileText className="w-4 h-4" strokeWidth={2.5} />;
      case 'invoice':
        return <Receipt className="w-4 h-4" strokeWidth={2.5} />;
      case 'client':
        return <Users className="w-4 h-4" strokeWidth={2.5} />;
      default:
        return <Clock className="w-4 h-4" strokeWidth={2.5} />;
    }
  };
  
  const getColor = () => {
    if (activity.action.includes('approved')) return 'bg-green-100 text-green-700';
    if (activity.action.includes('declined')) return 'bg-red-100 text-red-700';
    if (activity.action.includes('sent')) return 'bg-blue-100 text-blue-700';
    if (activity.action.includes('paid') || activity.action.includes('Payment')) return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };
  
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getColor()}`}>
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground">{activity.action}</p>
        {activity.user_name && (
          <p className="text-sm text-muted-foreground">by {activity.user_name}</p>
        )}
      </div>
      <p className="text-sm text-muted-foreground whitespace-nowrap">
        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
      </p>
    </div>
  );
};

const DashboardPage = () => {
  const { organization, user, hasPermission } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [clients, setClients] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tutorialStartStep, setTutorialStartStep] = useState(0);
  
  // Role checks
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const isStaff = user?.role === 'staff';

  // Check if we should show onboarding on first load
  // TEMPORARILY DISABLED - Tutorial will be replaced with welcome email onboarding
  useEffect(() => {
    // Tutorial disabled - keeping code for future re-enablement
    // To re-enable: remove the early return below
    return; // <-- DISABLED: Remove this line to re-enable tutorial
    
    if (!organization?.id) return;
    
    const onboardingKey = `onboarding_completed_${organization.id}`;
    const hasCompletedOnboarding = localStorage.getItem(onboardingKey);
    const tutorialPaused = localStorage.getItem(`tutorial_paused_${organization.id}`);
    const savedStep = localStorage.getItem(`tutorial_step_${organization.id}`);
    
    let timer;
    
    // Resume tutorial if it was paused (user clicked "Try It Now")
    if (tutorialPaused === 'true') {
      localStorage.removeItem(`tutorial_paused_${organization.id}`);
      const stepToResume = savedStep ? parseInt(savedStep, 10) : 0;
      setTutorialStartStep(stepToResume);
      // Longer delay to ensure state is updated before showing
      timer = setTimeout(() => setShowOnboarding(true), 600);
    } 
    // Resume from saved step if user was in the middle of tutorial
    else if (savedStep && parseInt(savedStep, 10) > 0 && !hasCompletedOnboarding) {
      const stepToResume = parseInt(savedStep, 10);
      setTutorialStartStep(stepToResume);
      timer = setTimeout(() => setShowOnboarding(true), 500);
    }
    // Show onboarding if: org has it enabled AND user hasn't completed it AND no saved step
    else if (organization?.preferences?.show_onboarding !== false && !hasCompletedOnboarding) {
      setTutorialStartStep(0);
      timer = setTimeout(() => setShowOnboarding(true), 500);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [organization?.id, organization?.preferences?.show_onboarding]);

  const handleOnboardingComplete = () => {
    const onboardingKey = `onboarding_completed_${organization?.id}`;
    localStorage.setItem(onboardingKey, 'true');
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    const onboardingKey = `onboarding_completed_${organization?.id}`;
    localStorage.setItem(onboardingKey, 'skipped');
    setShowOnboarding(false);
  };

  const loadDashboardData = useCallback(async () => {
    if (!organization?.id) return;
    
    try {
      // Staff doesn't need activity feed at all
      const activityRole = isAdmin ? '' : isManager ? 'manager' : 'staff';
      const fetchActivity = !isStaff;
      
      const promises = [
        apiHelpers.getStats(organization.id),
        fetchActivity ? apiHelpers.getActivity(organization.id, 10, activityRole) : Promise.resolve({ data: [] }),
        apiHelpers.getEstimates(organization.id, 'approved'),
        apiHelpers.getClients(organization.id)
      ];
      
      const [statsRes, activityRes, estimatesRes, clientsRes] = await Promise.all(promises);
      
      setStats(statsRes.data);
      setActivity(activityRes.data);
      
      // Get last 3 approved estimates as "jobs"
      setRecentJobs(estimatesRes.data.slice(0, 3));
      
      // Create client lookup
      const clientMap = {};
      clientsRes.data.forEach(c => { clientMap[c.id] = c; });
      setClients(clientMap);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard', { id: 'dashboard-error' });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, isAdmin, isManager, isStaff]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    await loadDashboardData();
    toast.success('Dashboard updated', { duration: 2000 });
  }, [loadDashboardData]);

  // Pull-to-refresh hook
  const { containerRef, isPulling, isRefreshing, pullDistance, pullProgress } = usePullToRefresh(
    handleRefresh,
    { threshold: 70, maxPull: 100, resistance: 2 }
  );

  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Helper function for compact currency formatting (mobile-friendly)
  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Calculate jobs in progress (approved estimates that haven't been fully invoiced/paid)
  const jobsInProgress = stats?.estimates?.approved || 0;
  const estimatesAwaitingApproval = stats?.estimates?.sent || 0;
  const outstandingBalance = stats?.financials?.outstanding || 0;
  const revenueCollected = stats?.financials?.total_collected || 0;

  return (
    <div 
      ref={containerRef}
      className="space-y-5 sm:space-y-8 animate-fade-in overflow-x-hidden" 
      data-testid="dashboard-page"
    >
      {/* Onboarding Tutorial */}
      {showOnboarding && (
        <OnboardingTutorial 
          key={`tutorial-${tutorialStartStep}`}
          onComplete={handleOnboardingComplete} 
          onSkip={handleOnboardingSkip}
          initialStep={tutorialStartStep}
        />
      )}
      
      {/* Pull to Refresh Indicator */}
      <PullToRefreshIndicator 
        pullDistance={pullDistance} 
        isRefreshing={isRefreshing} 
        pullProgress={pullProgress} 
      />
      
      {/* Header with subtle blueprint grid background */}
      <div 
        className="relative -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8 px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8 pb-5 sm:pb-6"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(30, 58, 138, 0.03) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(30, 58, 138, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px'
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground tracking-tight break-words">
              {organization?.company_name} {t('dashboard_title')}
            </h1>
            <p className="text-muted-foreground text-base sm:text-base md:text-lg mt-1">
              {t('dashboard_subtitle')}
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/site-visits?action=new')}
            className="h-14 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-2xl shadow-md transition-all hover:shadow-lg active:scale-95 w-full sm:w-auto sm:self-start"
            style={{ backgroundColor: accentColor, color: '#000' }}
            data-testid="dashboard-new-site-visit-btn"
          >
            <Plus className="w-6 h-6 mr-2" strokeWidth={2.5} />
            {t('new_site_visit')}
          </Button>
        </div>
      </div>
      
      {/* Stats Grid - Contractor focused */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          title={t('active_jobs')}
          value={jobsInProgress}
          subValue={t('approved_active')}
          icon={Hammer}
          color="bg-blue-600"
          link="/estimates?status=approved"
        />
        
        <StatCard
          title={t('pending')}
          value={estimatesAwaitingApproval}
          subValue={estimatesAwaitingApproval > 0 ? t('follow_up_soon') : t('all_caught_up')}
          icon={ClipboardList}
          color="bg-amber-500"
          link="/estimates?status=sent"
          urgent={estimatesAwaitingApproval > 3}
        />
        
        <StatCard
          title={t('outstanding')}
          value={formatCurrency(outstandingBalance)}
          subValue={`${(stats?.invoices?.unpaid || 0) + (stats?.invoices?.partial || 0)} ${t('unpaid')}`}
          icon={AlertCircle}
          color="bg-red-500"
          link="/invoices"
          urgent={outstandingBalance > 5000}
        />
        
        <StatCard
          title={t('revenue')}
          value={formatCurrency(revenueCollected)}
          subValue={`${formatCurrency(stats?.financials?.total_invoiced || 0)} ${t('invoiced')}`}
          icon={TrendingUp}
          color="bg-green-600"
          link="/invoices"
        />
      </div>
      
      {/* Estimate Review Queue (for managers/owners) */}
      {!isStaff && <EstimateReviewQueue />}
      
      {/* Quick Actions & Activity — Role-aware layout */}
      <div className={`grid grid-cols-1 ${isStaff ? '' : 'lg:grid-cols-3'} gap-4 sm:gap-6`}>
        {/* Quick Actions — filtered by role */}
        <div className={`${isStaff ? '' : 'lg:col-span-1'} space-y-4 sm:space-y-6`}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
            <h2 className="font-heading text-base sm:text-lg font-bold mb-4 uppercase tracking-wide text-muted-foreground">{t('quick_actions')}</h2>
            <div className={`${isStaff ? 'grid grid-cols-2 gap-3' : 'space-y-3'}`}>
              {/* Staff & all roles: View Jobs */}
              <Button
                onClick={() => navigate('/jobs')}
                className={`w-full ${isStaff ? 'h-20 flex-col gap-1' : 'h-14 justify-start'} text-base font-semibold rounded-xl border-2`}
                variant="outline"
                data-testid="quick-action-jobs"
              >
                <Hammer className={`${isStaff ? 'w-6 h-6' : 'w-5 h-5 mr-3'}`} style={{ color: primaryColor }} strokeWidth={2.5} />
                {t('jobs_title')}
              </Button>
              
              {/* Staff: View assigned site visits */}
              <Button
                onClick={() => navigate('/site-visits')}
                className={`w-full ${isStaff ? 'h-20 flex-col gap-1' : 'h-14 justify-start'} text-base font-semibold rounded-xl border-2`}
                variant="outline"
                data-testid="quick-action-site-visit"
              >
                <ClipboardCheck className={`${isStaff ? 'w-6 h-6' : 'w-5 h-5 mr-3'}`} style={{ color: primaryColor }} strokeWidth={2.5} />
                {isStaff ? t('site_visits_nav') : t('schedule_site_visit')}
              </Button>
              
              {/* Manager & Admin only */}
              {!isStaff && (
                <>
                  <Button
                    onClick={() => navigate('/estimates/new')}
                    className="w-full h-14 justify-start text-base font-semibold rounded-xl border-2"
                    variant="outline"
                    data-testid="quick-action-estimate"
                  >
                    <FileText className="w-5 h-5 mr-3" style={{ color: primaryColor }} strokeWidth={2.5} />
                    {t('quick_estimate')}
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/clients?action=new')}
                    className="w-full h-14 justify-start text-base font-semibold rounded-xl border-2"
                    variant="outline"
                    data-testid="quick-action-client"
                  >
                    <Users className="w-5 h-5 mr-3" style={{ color: primaryColor }} strokeWidth={2.5} />
                    {t('add_customer')}
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/jobs?action=new')}
                    className="w-full h-14 justify-start text-base font-semibold rounded-xl border-2"
                    variant="outline"
                    data-testid="quick-action-job"
                  >
                    <Hammer className="w-5 h-5 mr-3" style={{ color: primaryColor }} strokeWidth={2.5} />
                    {t('schedule_job')}
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {/* Estimate Pipeline — Manager & Admin only */}
          {!isStaff && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
            <h2 className="font-heading text-base sm:text-lg font-bold mb-4 uppercase tracking-wide text-muted-foreground">{t('estimate_pipeline')}</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-sm bg-gray-400" />
                  <span className="font-medium text-base">{t('draft')}</span>
                </div>
                <span className="font-bold text-xl">{stats?.estimates?.draft || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-sm bg-amber-500" />
                  <span className="font-medium text-base">{t('sent')}</span>
                </div>
                <span className="font-bold text-xl">{stats?.estimates?.sent || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-sm bg-green-500" />
                  <span className="font-medium text-base">{t('approved')}</span>
                </div>
                <span className="font-bold text-xl">{stats?.estimates?.approved || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-sm bg-red-500" />
                  <span className="font-medium text-base">{t('declined')}</span>
                </div>
                <span className="font-bold text-xl">{stats?.estimates?.declined || 0}</span>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground font-medium text-base">{t('customers_title')}</span>
                <span className="font-bold text-xl">{stats?.clients || 0}</span>
              </div>
            </div>
          </div>
          )}
          
          {/* Recent Jobs Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-base sm:text-lg font-bold uppercase tracking-wide text-muted-foreground">
                <Hammer className="w-5 h-5 inline mr-2" style={{ color: primaryColor }} />
                {t('active_jobs')}
              </h2>
              <Link 
                to="/estimates?status=approved"
                className="text-primary font-semibold text-sm hover:underline"
              >
                {t('view_all')}
              </Link>
            </div>
            
            {recentJobs.length > 0 ? (
              <div className="space-y-3">
                {recentJobs.map((job) => {
                  const client = clients[job.client_id];
                  return (
                    <button
                      key={job.id}
                      onClick={() => navigate(`/estimates/${job.id}`)}
                      className="w-full p-3 bg-blue-50 rounded-lg text-left hover:bg-blue-100 transition-colors group"
                      data-testid={`recent-job-${job.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground truncate group-hover:text-primary">
                            {job.service_type}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {client?.name || t('customers').slice(0, -1)}
                          </p>
                        </div>
                        <div className="text-right ml-3">
                          <p className="font-bold text-primary">${job.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <p className="text-xs text-green-600 font-medium">{t('approved').toUpperCase()}</p>
                        </div>
                      </div>
                      {job.job_address && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {job.job_address}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Hammer className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('no_jobs_found')}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Recent Activity — Manager & Admin only */}
        {!isStaff && (
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5 h-full">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="font-heading text-base sm:text-lg font-bold uppercase tracking-wide text-muted-foreground">{t('recent_activity')}</h2>
              {isManager && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 font-medium">Customer activity</span>
              )}
            </div>
            
            {activity.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {activity.map((item) => (
                  <ActivityItem key={item.id} activity={item} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground text-lg font-medium">{t('all_caught_up')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('no_results')}
                </p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
